
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Link2, Send, Check, X } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useSafeWebhook } from '@/hooks/useSafeWebhook';

const WebhookSettings = () => {
  const webhook = useSafeWebhook();
  const [inputUrl, setInputUrl] = useState(webhook?.webhookUrl || '');
  const [testLoading, setTestLoading] = useState(false);

  const handleSave = () => {
    if (!webhook?.setWebhookUrl) {
      toast({
        title: "Error",
        description: "Webhook settings are not available in this context",
        variant: "destructive",
      });
      return;
    }
    
    try {
      new URL(inputUrl); // Will throw if invalid
      webhook.setWebhookUrl(inputUrl);
      toast({
        title: "Webhook saved",
        description: "Your webhook URL has been saved",
      });
    } catch (e) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL",
        variant: "destructive",
      });
    }
  };

  const handleTest = async () => {
    if (!webhook?.triggerWebhook) {
      toast({
        title: "Error",
        description: "Webhook testing is not available in this context",
        variant: "destructive",
      });
      return;
    }
    
    setTestLoading(true);
    try {
      await webhook.triggerWebhook({
        event: "test",
        message: "This is a test webhook from bluetooth$"
      });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5" />
          Webhook Settings
        </CardTitle>
        <CardDescription>
          Configure a webhook to receive payment notifications from bluetooth$
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <label htmlFor="webhook-url" className="text-sm font-medium block mb-2">
              Webhook URL
            </label>
            <div className="flex gap-2">
              <Input
                id="webhook-url"
                placeholder="https://your-webhook-endpoint.com"
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                className="flex-1"
              />
              <Button variant="outline" onClick={handleSave}>
                Save
              </Button>
            </div>
            <div className="mt-2 flex items-center">
              {webhook?.webhookUrl ? (
                webhook.isWebhookValid?.() ? (
                  <div className="text-xs flex items-center gap-1 text-green-600">
                    <Check className="h-3 w-3" /> Valid webhook URL
                  </div>
                ) : (
                  <div className="text-xs flex items-center gap-1 text-red-600">
                    <X className="h-3 w-3" /> Invalid webhook URL
                  </div>
                )
              ) : (
                <div className="text-xs text-gray-500">
                  Enter a webhook URL to receive payment notifications
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-3 rounded-md">
            <h4 className="text-sm font-medium mb-2">What is a webhook?</h4>
            <p className="text-xs text-gray-600">
              A webhook allows external services to notify you when payments are received. 
              You can connect this to services like Zapier, Integromat, or your own custom backend.
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          variant="outline" 
          onClick={handleTest} 
          disabled={!webhook?.isWebhookValid?.() || testLoading}
          className="flex gap-2"
        >
          <Send className="h-4 w-4" />
          {testLoading ? "Sending..." : "Test Webhook"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default WebhookSettings;
